import React, { PureComponent } from 'react';

import './trail.scss';

import { TrailAPI } from '@philips-emr/tws-components-api';
import { Expression } from '@philips-emr/tws-constants';
import { AwesomeComponent, ComponentType } from '@philips-emr/tws-core';
import { arrayOf, bool, element, eventListener, expression, func, number, object, oneOfType, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { msg } from '@philips-emr/tws-core/lib/services/message-service';
import { classNames, isFunction, propagateProps } from '@philips-emr/tws-utils';

import { CardList, Link } from '..';

function Dot({ active, onClick, page }) {
  const dotClasses = {
    'c-trail__dot': true,
    'is-active': active,
  };

  return (
    <li className={classNames(dotClasses)} onClick={() => onClick(page)} />
  );
}

Dot.propTypes = {
  active: bool,
  onClick: func,
  page: number,
};

function Nav({ direction, onClick }) {
  const clasz = `c-trail__nav c-trail__nav--${direction} u-hide@xsm u-hide@sm`;

  return <div className={clasz} onClick={(event) => onClick(event)} />;
}

Nav.propTypes = {
  direction: string,
  onClick: func,
};

@AwesomeComponent({ type: ComponentType.LIST, api: TrailAPI })
class Trail extends PureComponent {
  state = {
    page: 0,
  };

  static propTypes = {
    canAddNewItem: bool,
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    data: arrayOf(object).notEnumerable(),
    dateVisible: bool,
    emptyResultSubtitle: expression,
    emptyResultTitle: expression,
    itemsPerPage: number,
    loading: bool,
    newItemText: expression,
    onClick: eventListener,
    onContextMenuClick: eventListener,
    onDoubleClick: eventListener,
    onNewCardClick: eventListener,
    onSeeAllClick: eventListener,
    seeAll: expression,
    showSeeAll: bool,
    title: expression,
    uuid: string,
  };

  static defaultProps = {
    canAddNewItem: true,
    data: [],
    dateVisible: true,
    emptyResultTitle: Expression.RESULTS_NOT_FOUND,
    itemsPerPage: 4,
    loading: false,
    newItemText: Expression.ADD_NEW,
    onNewCardClick: () => {},
    onSeeAllClick: () => {},
    seeAll: Expression.SEE_ALL_SECTIONS,
    showSeeAll: true,
    title: '',
  };

  getPageCount = () => {
    const { canAddNewItem, data, itemsPerPage } = this.props;

    let currentLength = data.length - 1;

    if (canAddNewItem) {
      currentLength++;
    }

    return Math.max(Math.floor(currentLength / itemsPerPage), 0);
  };

  getPlaceholderCount = () => {
    const { canAddNewItem, data, itemsPerPage } = this.props;

    const totalCards = canAddNewItem ? data.length + 1 : data.length;
    const difference = totalCards % itemsPerPage;

    if (difference) {
      return itemsPerPage - difference;
    }

    return 0;
  };

  incrementPage = (increment) => {
    const { page } = this.state;
    const nextPage = page + increment;

    const isValid =
      nextPage != page && (nextPage >= 0 && nextPage <= this.getPageCount());

    if (isValid) {
      this.updatePage(nextPage);
    }
  };

  updatePage = (page) => {
    this.setState({
      page: page,
    });
  };

  onSeeAllClick = () => {
    const { onSeeAllClick } = this.props;

    onSeeAllClick && onSeeAllClick();
  };

  onNewCardClick = () => {
    const { onNewCardClick } = this.props;

    onNewCardClick && onNewCardClick();
  };

  onClick = (card) => {
    const { onClick } = this.props;

    isFunction(onClick) && onClick({ value: card });
  };

  onDoubleClick = (card) => {
    const { onDoubleClick } = this.props;

    isFunction(onDoubleClick) && onDoubleClick({ value: card });
  };

  onContextMenuClick = (card) => {
    const { onContextMenuClick } = this.props;

    isFunction(onContextMenuClick) && onContextMenuClick({ value: card });
  };

  renderDots = () => {
    const { loading } = this.props;
    const { page } = this.state;

    const pageCount = this.getPageCount();

    if (loading || !pageCount) {
      return null;
    }

    const elements = [];
    for (let index = 0; index <= pageCount; index++) {
      elements.push(
        <Dot
          active={index === page}
          key={index}
          onClick={this.updatePage}
          page={index}
        />
      );
    }

    return <ul className="c-trail__dots">{elements}</ul>;
  };

  renderNavPrev = () => {
    const { data, loading } = this.props;
    const { page } = this.state;

    if (loading || page === 0 || !data.length) {
      return null;
    }

    return <Nav direction="prev" onClick={() => this.incrementPage(-1)} />;
  };

  renderNavNext = () => {
    const { data, loading } = this.props;
    const { page } = this.state;

    if (loading || page === this.getPageCount() || !data.length) {
      return null;
    }

    return <Nav direction="next" onClick={() => this.incrementPage(1)} />;
  };

  renderCards = () => {
    const { canAddNewItem, children, data, dateVisible, emptyResultSubtitle, emptyResultTitle, loading, newItemText, uuid } = this.props;
    const { page } = this.state;

    const percentage = page * 100 * -1;
    const sliderStyle = {
      transform: `translateX(calc(${percentage}% - calc(${page} * var(--col-gap)))`,
    };

    return (
      <CardList
        addNewExpression={newItemText}
        className="c-trail__slider"
        data={data}
        emptyResultSubtitleExpression={emptyResultSubtitle}
        emptyResultTitleExpression={emptyResultTitle}
        loading={loading}
        noWrap
        onAddNewClick={this.onNewCardClick}
        onClick={this.onClick}
        onContextMenuClick={this.onContextMenuClick}
        onDoubleClick={this.onDoubleClick}
        placeholderCount={this.getPlaceholderCount()}
        showAddNew={canAddNewItem}
        showDates={dateVisible}
        style={sliderStyle}
        uuid={uuid}
      >
        {propagateProps(children, { className: 'u-grid-col-4 u-grid-col-3@lg' })}
      </CardList>
    );
  };

  renderSeeAllLink = () => {
    const { data, loading, seeAll, showSeeAll } = this.props;

    if (!showSeeAll || loading || !data.length) {
      return null;
    }

    return (
      <Link size="md" className="c-trail__all" onClick={this.onSeeAllClick}>
        {msg(seeAll)}
      </Link>
    );
  };

  render() {
    const { dateVisible, loading, title } = this.props;

    const rootClasses = {
      'c-trail': true,
      'c-trail--no-date': !dateVisible,
      'is-loading': loading,
    };

    return (
      <div className={classNames(rootClasses)}>
        {title.trim() &&
        <div className="c-trail__title">{msg(title)}</div>}

        <div className="c-trail__container">
          {this.renderNavPrev()}

          <div className="c-trail__viewport">
            {this.renderCards()}
          </div>

          {this.renderNavNext()}
        </div>

        <div className="c-trail__footer u-hide@xsm u-hide@sm">
          {this.renderDots()}
          {this.renderSeeAllLink()}
        </div>
      </div>
    );
  }
}

export default Trail;
